import React from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import {
    Button,
    Col,
    Glyphicon,
    Row,
    SponsoredText,
    Text,
} from 'reaxl';
import { useFeatures } from 'reaxl-features';

import { getAlphaCriteriaMatches } from '@/utilities/getAlphaCriteriaMatches';

import { srpFiltersDuck } from '@/ducks/srp';

function AlphaShowcaseInfoText({
    dealerName = '',
    onHideAlpha,
    showcaseListing = {},
    uiContext = 'inventory',
}) {
    const {
        show_alpha_criteria: [enableUnmatchedCriteria],
        srp_alpha_suppress_button: [isAlphaSuppressEnabled],
    } = useFeatures([
        'show_alpha_criteria',
        'srp_alpha_suppress_button',
    ]);

    const byDealerNameText = dealerName && `Sponsored by ${dealerName}`;
    const isInventoryAlpha = uiContext === 'inventory';
    const hideAlphaLinkText = 'Hide';

    const filterChips = useSelector(srpFiltersDuck.selectors.getSelectedFilterChips);
    const filtersValues = useSelector(srpFiltersDuck.selectors.getFiltersValues);

    const { missingCriteria } = enableUnmatchedCriteria ? getAlphaCriteriaMatches({
        filtersValues, filterChips, showcaseListing,
    }) : [];

    const renderMissingCriteria = () => (missingCriteria.length > 0 ? (
        <div
            className="text-overflow w-100"
            data-cmp="txt-alpha-criteria"
        >
            <Text
                className="padding-right-2"
                color="gray"
                size={100}
            >
                Missing:
            </Text>
            {missingCriteria.map((criteria, index) => (
                <Text
                    className="padding-right-2"
                    color="gray"
                    decoration="line-through"
                    key={index}
                    size={100}
                >
                    {criteria}
                </Text>
            ))}
        </div>
    ) : null);

    const sponsoredTextAndCloseButton = (
        <>
            {uiContext === 'owner' && (
                <SponsoredText />
            )}
            <Col
                className="display-flex align-items-center padding-0"
                xs={10}
            >
                {isInventoryAlpha && byDealerNameText}
                {uiContext === 'owner' && (
                    <div className="text-size-300 text-bold">Featured Dealer Near You</div>
                )}
            </Col>
            <Col
                className="display-flex align-items-center justify-content-end padding-0 text-link link-undecorated"
                xs={2}
            >
                {isAlphaSuppressEnabled && (
                    <Button
                        bsStyle="text"
                        bsSize="small"
                        className="display-flex padding-0 text-size-100"
                        onClick={onHideAlpha}
                    >
                        {hideAlphaLinkText}
                        <Glyphicon
                            aria-label="remove alpha"
                            glyph="remove"
                            role="button"
                            className="text-blue text-size-300"
                        />
                    </Button>
                )}
            </Col>
        </>
    );

    const textPlaceholderHeight = uiContext === 'owner' ? '46px' : '24px';
    const showMissingCriteria = enableUnmatchedCriteria && uiContext === 'inventory';

    return (
        <Row className="padding-horizontal-2">
            <Col
                key="alphaSponsoredText"
                className="padding-0 text-size-100"
                xs={12}
                data-cmp="txt-alpha-info"
                style={{ height: textPlaceholderHeight }}
            >
                { showMissingCriteria ? renderMissingCriteria() : sponsoredTextAndCloseButton}
            </Col>
        </Row>
    );
}

AlphaShowcaseInfoText.propTypes = {
    /**
     * Notifies the AlphaShowcase whether to display owner or invertory Alpha listing
     */
    uiContext: PropTypes.oneOf([
        'owner',
        'inventory',
    ]),
    /**
     * The dealer name
     */
    dealerName: PropTypes.string,
    /**
     * Callback function to hide the Alpha showcase
     */
    onHideAlpha: PropTypes.func,
    /**
     * The alpha listing object
     */
    showcaseListing: PropTypes.object,
};

export default AlphaShowcaseInfoText;
